import axios, { type AxiosError, type InternalAxiosRequestConfig, type AxiosInstance } from 'axios';
import { recordBreadcrumb } from '@lumi/ui';
import { useAuthStore } from '../stores/auth';
import {
  DEMO_PERSONA_HEADER,
  demoPersonaHeaderValue,
  dropDemoPersonaOnRefusal,
  readActiveRoleId,
  installDataChangedSignal,
  installHtmlResponseGuard,
  noteTenantUnavailability,
} from '@lumi/app-kit';
import {
  runtimeMasterApiBaseUrl,
  runtimeUserApiBaseUrl,
  runtimeSupportApiBaseUrl,
  runtimeNotificationApiBaseUrl,
  runtimeAssistantApiBaseUrl,
  runtimeAtsApiBaseUrl,
  runtimeTimeApiBaseUrl,
  runtimeApprenticeApiBaseUrl,
  runtimePeopleApiBaseUrl,
  currentTenantSlug,
} from './runtime-config';

// Per-request opt-out for the global notice surface: a caller that shows its own
// error for a request sets `{ suppressGlobalError: true }` so the interceptor
// stays silent and the user sees one notice, not two.
//
// `keepDemoPersona` is the same kind of opt-out for the mask: a question whose
// refusal is itself the answer (may this persona manage X?) sets it, so a
// persona with no seat in that module reads as "no" instead of dropping the
// worn persona for the whole tab (issue 807).
declare module 'axios' {
  export interface AxiosRequestConfig {
    suppressGlobalError?: boolean;
    keepDemoPersona?: boolean;
  }
}

function normalizeBaseUrl(raw: string | undefined, fallback: string): string {
  const trimmed = (raw ?? '').trim().replace(/^['"]+|['"]+$/g, '');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  if (!trimmed || trimmed === '/') return origin ? `${origin}${fallback}` : fallback;
  const clean = trimmed.replace(/\/+$/, '');
  if (/\/api(\/master|\/user|\/support)?$/.test(clean)) return clean;
  return `${clean}${fallback}`;
}

export const masterApi = axios.create({
  baseURL: normalizeBaseUrl(runtimeMasterApiBaseUrl(), '/api/master'),
  timeout: 15000,
});

export const userApi = axios.create({
  baseURL: normalizeBaseUrl(runtimeUserApiBaseUrl(), '/api/user'),
  timeout: 15000,
});

// Tenant-scoped support ticket API. Same bearer-auth + global-error pattern as
// masterApi; callers attach the `X-Tenant-Slug` header per request (the support
// store does this) since one client serves every tenant the user belongs to.
export const supportApi = axios.create({
  baseURL: normalizeBaseUrl(runtimeSupportApiBaseUrl(), '/api/support'),
  timeout: 15000,
});

masterApi.interceptors.request.use((config) => {
  const authStore = useAuthStore();
  if (authStore.token) {
    config.headers.Authorization = `Bearer ${authStore.token}`;
  }
  return config;
});

userApi.interceptors.request.use((config) => {
  const authStore = useAuthStore();
  if (authStore.token) {
    config.headers.Authorization = `Bearer ${authStore.token}`;
  }
  return config;
});

supportApi.interceptors.request.use((config) => {
  const authStore = useAuthStore();
  if (authStore.token) {
    config.headers.Authorization = `Bearer ${authStore.token}`;
  }
  return config;
});

let refreshPromise: Promise<string | null> | null = null;

async function tryRefreshToken(): Promise<string | null> {
  const authStore = useAuthStore();
  const rt = authStore.refreshToken;
  if (!rt) return null;
  try {
    // seam-exempt: transport — silent 401 token refresh, run from the interceptor.
    const res = await userApi.post('/auth/refresh', { refreshToken: rt });
    const { token: newToken, refreshToken: newRefresh } = res.data || {};
    if (newToken) {
      authStore.setToken(newToken);
      authStore.setRefreshToken(newRefresh || null);
      return newToken;
    }
  } catch {
    // refresh failed
  }
  return null;
}

// Bootstrap session repair (router guard): an access token that is missing or
// expired while a refresh token exists is a REPAIRABLE session, and this state
// occurs routinely — the suite shares one origin (and thus one localStorage)
// per tenant host, and a module UI without refresh support that 401-logs-out
// removes only the `token` key. Without repair, `isAuthenticated` passes on
// the refresh token alone and authenticated routes render off the half-dead
// session (empty tenant list, "?" avatar) with no request in flight that the
// 401 interceptor could ever heal. Shares `refreshPromise` with that
// interceptor so concurrent callers coalesce onto one rotation.
export async function ensureFreshSession(): Promise<boolean> {
  const authStore = useAuthStore();
  if (authStore.isTokenValid(authStore.token)) return true;
  if (!authStore.refreshToken) return false;
  if (!refreshPromise) {
    refreshPromise = tryRefreshToken().finally(() => { refreshPromise = null; });
  }
  return (await refreshPromise) !== null;
}

function makeErrorHandler() {
  return (error: AxiosError) => {
    const authStore = useAuthStore();
    const url = error.config?.url || '';
    // The sign-in ceremonies too, not only login and refresh (issue 619): a wrong
    // second-factor code (`/auth/mfa/verify`, 401 `invalid_mfa_code`) or an
    // operator's passkey without user verification (`/auth/passkey/verify`) is a
    // refusal the login view renders. Not every `/auth/` route: `/auth/logout`,
    // `/auth/methods` and the email change carry the session, and a 401 there IS
    // one that has died. Spelled as `url.includes(...)` calls: that is the form
    // scripts/check-ui-auth-ladder.mjs reads as the refresh exemption.
    const isAuthEndpoint =
      url.includes('/auth/login') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/mfa/') ||
      url.includes('/auth/passkey/');

    // Feed the shared breadcrumb trail from this existing hook so a later support
    // ticket carries the recent failures. Passive — no logging, no new listeners.
    recordBreadcrumb({ type: 'request_error', url, status: error.response?.status ?? 0 });

    // The workspace itself is unreachable (issue 364 F57): every module API
    // answers a SUSPENDED tenant and an UNKNOWN slug with the same `404 TENANT_NOT_FOUND`,
    // and until this hook existed every read simply 404'd and the app drew its
    // empty states around nothing. Raises on that ONE code — a plain 404 still
    // means "this record is gone". App.vue renders the page instead of the routes.
    noteTenantUnavailability(error);

    // A 401 from the auth endpoints themselves means "bad credentials" or an
    // expired refresh token — not a live session that can be refreshed. Let it
    // propagate to the caller (e.g. the login view's own error handling) instead
    // of triggering a logout/redirect, which reloads the page and wipes the
    // rendered error before the user can see it. The auth endpoints surface their
    // own errors locally, so the global fallback stays silent here too.
    if (error.response?.status === 401 && isAuthEndpoint) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !(error.config as any)?._isRetry && authStore.refreshToken) {
      (error.config as any)._isRetry = true;
      if (!refreshPromise) {
        refreshPromise = tryRefreshToken().finally(() => { refreshPromise = null; });
      }
      return refreshPromise.then((newToken) => {
        if (!newToken) {
          authStore.logout();
          window.location.href = '/login';
          return Promise.reject(error);
        }
        const config = error.config as InternalAxiosRequestConfig;
        config.headers.Authorization = `Bearer ${newToken}`;
        return axios.request(config);
      });
    }
    if (error.response?.status === 401) {
      authStore.logout();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Write failures are NOT surfaced here any more. workspace-ui is converted
    // (issue 195, ADR 0068 rule 8): every write runs through `useApiMutation`,
    // which owns the failure channel — a global notice, the form, or a named
    // consumer. Leaving this call in place reported the same failure twice, once
    // from the seam and once from here, for every in-place site (addendum A10).
    // Reads were already silent here by design (ADR 0068 rule 5).
    return Promise.reject(error);
  };
}

masterApi.interceptors.response.use((r) => r, makeErrorHandler());
userApi.interceptors.response.use((r) => r, makeErrorHandler());
supportApi.interceptors.response.use((r) => r, makeErrorHandler());

// HTML-instead-of-JSON guard (#317 A73). A misrouted request — a wrong VITE_*
// base URL, a proxy rule that fell through, an ingress serving the SPA shell for
// an /api path — answers 200 with an HTML error page. Without this, axios throws
// mid-parse or (in the copy that once dropped the check) resolves it as a
// SUCCESSFUL response, so a broken deploy looks healthy from the browser. The
// three clients above are built directly; the tenant-scoped factory installs it
// for the rest.
for (const client of [masterApi, userApi, supportApi]) {
  installHtmlResponseGuard(client);
}

// Tenant-scoped module clients. Each module API is reached same-origin under its
// path prefix (ADR 0013): notification `/api/notification`, ats `/api`, time
// `/api/time`, apprentice `/api/apprentice`, people `/api/people`. All carry the user JWT + `X-Tenant-Slug` and route errors
// through the shared handler. The
// homebase fan-out callers pass `{ suppressGlobalError: true }` so a module that
// has no data / isn't reachable degrades to a plain launcher tile.
function moduleApiBaseUrl(raw: string, fallback: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const trimmed = (raw ?? '').trim().replace(/^['"]+|['"]+$/g, '');
  const val = !trimmed || trimmed === '/' ? fallback : trimmed;
  if (/^https?:\/\//.test(val)) return val.replace(/\/+$/, '');
  const clean = `/${val.replace(/^\/+|\/+$/g, '')}`;
  return origin ? `${origin}${clean}` : clean;
}

// Which tenant-scoped clients carry the demo persona (#409). Only the four
// seeded module APIs and Lumi map it to a synthetic seat (and allow the header
// in CORS); the rest — notification, payment, privacy — are off-stage
// or unseeded and never see it, so a persona cannot even be offered to them.
type PersonaCarrier = 'persona' | 'no-persona';

function tenantScopedApi(baseURL: string, roleModule?: string, carrier: PersonaCarrier = 'no-persona'): AxiosInstance {
  const instance = axios.create({ baseURL, timeout: 15000 });
  instance.interceptors.request.use((config) => {
    const authStore = useAuthStore();
    if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`;
    }
    const slug = currentTenantSlug();
    if (slug) {
      config.headers['X-Tenant-Slug'] = slug;
    }
    // X-Active-Role-Id (#317 A78). Three of the clients built here — atsApi,
    // peopleApi, apprenticeApi — hit services that read this header, and without
    // it ats-api falls back to `roleIds[0]`: the user silently acts under
    // whichever role happens to sort first rather than the one they picked. Not
    // an open door, but the wrong permissions.
    //
    // Read through app-kit's stamped reader, so a value left by a previous
    // account in this shared-origin localStorage is refused rather than sent.
    // Role ids are MODULE-scoped: each client reads the slot of the API it
    // calls (ADR 0062 — one app, several module APIs), never another module's.
    const activeRoleId = readActiveRoleId(slug, authStore.user?.userId, roleModule);
    if (activeRoleId) {
      config.headers['X-Active-Role-Id'] = activeRoleId;
    }
    if (carrier === 'persona') {
      // Stamped read, like the role id: the launcher shows the persona's modules
      // and greets by the persona's name only while the module APIs see the same
      // mask the module UIs send.
      const persona = demoPersonaHeaderValue(slug, authStore.user?.userId);
      if (persona) config.headers[DEMO_PERSONA_HEADER] = persona;
    }
    return config;
  });
  const onError = makeErrorHandler();
  instance.interceptors.response.use((r) => r, (error: AxiosError) => {
    // A refused persona (the demo ended under this tab, or no seat in that module)
    // drops the mask and reloads once; it is not a session problem.
    if (carrier === 'persona' && !error.config?.keepDemoPersona && dropDemoPersonaOnRefusal(error, currentTenantSlug())) {
      return Promise.reject(error);
    }
    return onError(error);
  });
  // Every client built by this factory gets it, so a new one added below cannot
  // be the one that goes without (#317 A73).
  installHtmlResponseGuard(instance);
  // The data-changed signal (#460): a successful write tells the demo bar to
  // re-read the journey now instead of on its next poll. On the persona carriers
  // only — the module APIs and Lumi are where a journey task can be ticked, and a
  // notification marked read must not re-read every module's checks. After the
  // guard, so a write answered with an HTML page never raises it.
  if (carrier === 'persona') installDataChangedSignal(instance, roleModule ?? 'assistant');
  return instance;
}

export const notificationApi = tenantScopedApi(moduleApiBaseUrl(runtimeNotificationApiBaseUrl(), '/api/notification'));
export const assistantApi = tenantScopedApi(moduleApiBaseUrl(runtimeAssistantApiBaseUrl(), '/api/assistant'), undefined, 'persona');
export const atsApi = tenantScopedApi(moduleApiBaseUrl(runtimeAtsApiBaseUrl(), '/api'), 'ats', 'persona');
export const timeApi = tenantScopedApi(moduleApiBaseUrl(runtimeTimeApiBaseUrl(), '/api/time'), 'time', 'persona');
export const apprenticeApi = tenantScopedApi(moduleApiBaseUrl(runtimeApprenticeApiBaseUrl(), '/api/apprentice'), 'apprentice', 'persona');
export const peopleApi = tenantScopedApi(moduleApiBaseUrl(runtimePeopleApiBaseUrl(), '/api/people'), 'people', 'persona');
// The REAL user's own People record, never a worn demo persona's: onboarding's
// "Your details" confirms the owner herself (ADR 0088 §8.3), and a persona header
// would answer — and confirm — a cast member's record instead.
export const peopleSelfApi = tenantScopedApi(moduleApiBaseUrl(runtimePeopleApiBaseUrl(), '/api/people'), 'people');

// payment-api tenant billing routes (ADR 0023 Decision 8): same-origin `/api/payment`.
// tenantScopedApi already wires the shared error handler (401 auto-refresh +
// unified notices), so the Billing tab (BillingView) is covered like the others.
export const paymentApi = tenantScopedApi(moduleApiBaseUrl('', '/api/payment'));
// privacy-api's person-facing my-data routes (issue 241, D20/D21): same-origin
// `/api/privacy` on the per-tenant host, exactly like payment-api above. Both of
// its routes are guarded by JwtAuthGuard + WorkspaceMembershipGuard and resolve
// the tenant from `X-Tenant-Slug`, which tenantScopedApi attaches — so this is a
// tenant-scoped client and never a bearer-only one.
//
// NO RUNTIME BASE-URL KEY, deliberately. `PRIVACY_API_BASE_URL` is already taken
// repo-wide as the name of the INTERNAL cluster URL `http://privacy-api:3016/api/
// privacy` (people-api / time-api / worker / system-admin-api read it; see
// docs/operations/environment-variables.md). Handing that same name to this
// browser bundle would inject a hostname no browser can resolve. The tenant host
// already routes `/api/privacy` to privacy-api at priority 100
// (architecture.manifest.json, privacy-api `routing`), so same-origin is both
// correct and unconfigurable — the payment-api precedent, for the same reason.
export const privacyApi = tenantScopedApi(moduleApiBaseUrl('', '/api/privacy'));
// audit-api's tenant read surface (ADR 0028) — the Administration section's audit
// log (#432), which moved here with the rest of the tenant admin pages. Same-origin
// `/api/audit` on the per-tenant host, which Traefik path-routes at priority 100
// ahead of this root app; no runtime base-url key, for the same reason payment-api
// and privacy-api have none — the path is routed by the ingress, so it is both
// correct and unconfigurable. Tenant-scoped: every read is scoped to the signed-in
// user plus `X-Tenant-Slug`, which the factory attaches.
export const auditApi = tenantScopedApi(moduleApiBaseUrl('', '/api/audit'));
